/**
 * Apply imported statements and documents to the wizard's form data.
 *
 * Each upload's checked result (from POST /wizard/import) is kept, compacted,
 * in `formData.import.files`. Every entry in a file — a merchant from a
 * statement, or a figure read from a document — carries the field it belongs
 * to and whether it is *added* (the classifier was confident) or only
 * *suggested* ("not sure"). Alongside the files:
 *   - `import.accepted`  — entries the client added with +: entry key -> "section.field".
 *   - `import.dismissed` — what the client removed with x: "section.field",
 *                          "item:<entry key>" (an income source or auto loan) or
 *                          "income:<payer>" (a deposit-based Income name).
 *   - `import.applied`   — what the import last wrote, so it can tell its own
 *                          values from the client's.
 *
 * Statements are combined per account before scaling to a year: three
 * monthly statements for "Card ending 0059" cover ~92 days together, so their
 * amounts are added and scaled by 365/92 once — not each scaled by ~12.
 * Different accounts are then added together; year-end summaries and
 * documents are already annual and stand alone.
 *
 * A field is filled only when it is empty, or still holds exactly what the
 * import last put there. Anything the client typed — including a deliberate
 * 0 — is theirs and is never overwritten. So adding a file tops up the
 * untouched fields, and removing one takes back only its untouched share.
 *
 * Two targets are special: "income.source" adds an Income source (a W-2 or
 * pay stub gives the gross amount), and "auto_transport.auto_loan" fills the
 * first free Auto Lease / Loan slot. Bank deposits only suggest Income
 * *names* (they're take-home pay; the page asks for gross).
 */

import { AUTO_LOAN, IMPORT_AUTO_FLAGS, IMPORT_TARGETS, INCOME_SOURCE } from './importTargets';

/** Most files one form keeps — form_data is a single DynamoDB item (400 KB). */
export const MAX_IMPORT_FILES = 36;
/** Refuse further uploads once form_data would pass this many characters. */
export const MAX_FORM_JSON = 350 * 1024;

const MIN_INCOME_PER_YEAR = 1000;
const AUTO_SLOTS = ['auto_1', 'auto_2', 'auto_3'];

// Entry flags, stored as one number per compact entry.
const ADDED = 1;
const PAYROLL = 2;
const LEASE = 4;

// Field names are unique across sections, so records store just "gas_fuel".
const TARGET_BY_FIELD = Object.fromEntries(
  Object.entries(IMPORT_TARGETS).map(([key, t]) => [t.field, key]),
);

/** Entry key, as the backend builds merchant keys: "Trader Joe's" -> "trader joe s". */
export function merchantKey(label) {
  return (label || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** True when saving `formData` would come too close to the storage limit. */
export function importTooLarge(formData) {
  return JSON.stringify(formData).length > MAX_FORM_JSON;
}

function importState(formData) {
  const state = formData?.import || {};
  return {
    files: state.files || [],
    applied: state.applied || {},
    accepted: state.accepted || {},
    dismissed: state.dismissed || {},
  };
}

function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

/**
 * The compact record kept in form_data for one checked result. Entries become
 * [label, field, raw, months, category, flags] (flags: 1 added, 2 deducted
 * from wages, 4 lease) and deposits [key, label, kind, raw, months]; keys are
 * rebuilt from labels and the response's derived summaries are dropped.
 */
export function compactRecord(result, { id, importedAt }) {
  return {
    id,
    filename: result.filename,
    imported_at: importedAt,
    source_type: result.source_type,
    layout: result.layout,
    doc_kind: result.doc_kind || 'transactions',
    doc_type: result.doc_type || null,
    import_type: result.import_type || null,
    classifier: result.classifier || null,
    review_notice: result.review_notice || null,
    is_annual: !!result.is_annual,
    period_start: result.period_start || null,
    period_end: result.period_end || null,
    account_key: result.account_key || null,
    account_label: result.account_label || null,
    warnings: result.warnings || [],
    m: (result.merchants || []).map((x) => [
      x.label,
      x.target ? x.target.split('.')[1] : null,
      x.raw_amount,
      x.months || [],
      x.category || '',
      ((x.auto_add ?? !!x.target) ? ADDED : 0) | (x.payroll ? PAYROLL : 0) | (x.lease ? LEASE : 0),
    ]),
    inc: (result.income || []).map((x) => [x.key, x.label, x.kind, x.raw_amount, x.months || []]),
  };
}

/** A file's entries as objects; older records fall back gracefully. */
export function merchantsOf(file) {
  if (file.m) {
    return file.m.map(([label, field, raw, months, category, flags]) => {
      // Records from before flags existed: anything with a field was added.
      const bits = flags === undefined ? (field ? ADDED : 0) : flags;
      return {
        key: merchantKey(label), label, raw, months: months || [], category: category || '',
        target: field ? TARGET_BY_FIELD[field] || null : null,
        added: !!(bits & ADDED), payroll: !!(bits & PAYROLL), lease: !!(bits & LEASE),
      };
    });
  }
  if (file.merchants) {
    return file.merchants.map((x) => ({
      key: merchantKey(x.label), label: x.label, raw: x.raw_amount, target: x.target,
      months: x.months || [], category: x.category || '',
      added: x.auto_add ?? !!x.target, payroll: !!x.payroll, lease: !!x.lease,
    }));
  }
  // Records saved before per-merchant detail existed.
  return (file.fields || []).map((f) => ({
    key: `field:${f.section}.${f.field}`,
    label: f.field_label || `${f.section}.${f.field}`,
    raw: f.raw_amount !== undefined ? f.raw_amount : f.amount,
    target: `${f.section}.${f.field}`,
    months: [],
    category: '',
    added: true,
    legacy: f.raw_amount === undefined,
  }));
}

function incomeOf(file) {
  if (file.inc) return file.inc.map(([key, label, kind, raw, months]) => ({ key, label, kind, raw, months }));
  return (file.income || []).map((x) => ({ ...x, raw: x.raw_amount }));
}

/**
 * Scale factor from covered days to one year. Mirrors annual_factor in
 * backend/service_client_forms/cashflow_import/transactions.py.
 */
export function annualFactor(days) {
  if (days < 28) return 12;
  if (days < 330) return 365 / days;
  if (days <= 400) return 1;
  return 365 / days;
}

function dayNumber(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
}

/** Days covered by a set of [start, end] ISO ranges, counting overlaps once. */
export function coveredDays(ranges) {
  const spans = ranges
    .map(([start, end]) => [dayNumber(start), dayNumber(end)])
    .sort((a, b) => a[0] - b[0]);
  let total = 0;
  let current = null;
  for (const [start, end] of spans) {
    if (current && start <= current[1] + 1) {
      current[1] = Math.max(current[1], end);
    } else {
      if (current) total += current[1] - current[0] + 1;
      current = [start, end];
    }
  }
  if (current) total += current[1] - current[0] + 1;
  return total;
}

function isLegacy(file) {
  return !file.m && !file.merchants && (file.fields || []).some((f) => f.raw_amount === undefined);
}

function standsAlone(file) {
  return file.is_annual || isLegacy(file) || !file.period_start || !file.period_end;
}

/**
 * Ids of documents a newer statement of the same account has replaced.
 *
 * A mortgage, loan or plan statement states a rate (this month's payment,
 * this quarter's contributions), so two statements of one account are two
 * readings of the same figure: the latest wins instead of the two adding up.
 * Latest = the later statement period, then the later upload. The import
 * type is part of the account key — two kinds of document can share last-4
 * digits. Bank and card statements are combined over time instead (see
 * importGroups).
 */
export function replacedFiles(files) {
  const latest = new Map();
  (files || []).forEach((file, index) => {
    if (file.doc_kind !== 'document' || !file.account_key) return;
    const key = `${file.import_type || file.doc_type || ''}|${file.account_key}`;
    const rank = [file.period_end || '', file.imported_at || '', index];
    const best = latest.get(key);
    if (!best || compareRanks(rank, best.rank) > 0) latest.set(key, { id: file.id, rank });
  });
  const keep = new Set([...latest.values()].map((entry) => entry.id));
  return new Set(
    (files || [])
      .filter((file) => file.doc_kind === 'document' && file.account_key && !keep.has(file.id))
      .map((file) => file.id),
  );
}

function compareRanks(a, b) {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] > b[i] ? 1 : -1;
  }
  return 0;
}

/** The files that count: all of them except documents a newer statement replaced. */
export function currentFiles(files) {
  const replaced = replacedFiles(files);
  return (files || []).filter((file) => !replaced.has(file.id));
}

/**
 * Imported files grouped by account, with each group's covered days and
 * scale factor. Year-end summaries, documents, undated files and files
 * without an account each form their own group. Groups keep upload order.
 */
export function importGroups(files) {
  const groups = new Map();
  for (const file of files || []) {
    const key = !standsAlone(file) && file.account_key ? `account:${file.account_key}` : `file:${file.id}`;
    if (!groups.has(key)) {
      groups.set(key, { key, label: file.account_label || file.filename, files: [] });
    }
    groups.get(key).files.push(file);
  }
  return [...groups.values()].map((group) => {
    if (standsAlone(group.files[0])) {
      return { ...group, days: null, factor: 1, annual: true };
    }
    const days = coveredDays(group.files.map((f) => [f.period_start, f.period_end]));
    return { ...group, days, factor: annualFactor(days), annual: false };
  });
}

function isSpecial(target) {
  return target === INCOME_SOURCE || target === AUTO_LOAN;
}

// Which document is the best source of an employer's gross pay (lower wins).
const INCOME_DOC_PRIORITY = {
  w2: 1, pay_stub: 1, payroll_or_tax: 1, social_security_statement: 1, pension_statement: 1,
  tax_return: 2, benefits_summary: 3, benefits: 3,
};

function incomePriority(file) {
  return INCOME_DOC_PRIORITY[file.doc_type] || 4;
}

/** The x key for an entry placed in `target`. */
function dismissKeyFor(target, entryKey) {
  return isSpecial(target) ? `item:${entryKey}` : target;
}

/**
 * Every entry across the imports, combined by key with its yearly amount
 * (raw x its account group's factor) and where it ends up:
 *   effective — the field it fills now (null when "not sure" or removed),
 *   suggested — the field to offer on + (may be null: the picker opens).
 * `monthly` marks a merchant seen in three or more months of one account.
 */
export function importMerchants(files, state = {}) {
  const accepted = state.accepted || {};
  const dismissed = state.dismissed || {};
  const merged = new Map();
  for (const group of importGroups(currentFiles(files))) {
    const perGroup = new Map();
    for (const file of group.files) {
      for (const m of merchantsOf(file)) {
        const annual = m.legacy ? Number(m.raw) || 0 : (Number(m.raw) || 0) * group.factor;
        const entry = merged.get(m.key) || {
          key: m.key, label: m.label, category: m.category, target: m.target,
          annual: 0, monthly: false, added: false, payroll: false, lease: false, files: [], priority: 9,
        };
        entry.annual += annual;
        entry.priority = Math.min(entry.priority, incomePriority(file));
        entry.added = entry.added || (m.added && !!m.target);
        entry.target = entry.target || m.target;
        entry.payroll = entry.payroll || !!m.payroll;
        entry.lease = entry.lease || !!m.lease;
        if (!entry.files.includes(file.filename)) entry.files.push(file.filename);
        merged.set(m.key, entry);
        const months = perGroup.get(m.key) || new Set();
        (m.months || []).forEach((month) => months.add(month));
        perGroup.set(m.key, months);
      }
    }
    for (const [key, months] of perGroup) {
      if (months.size >= 3) merged.get(key).monthly = true;
    }
  }
  const entries = [...merged.values()].map((m) => {
    // A + choice always counts; an automatic one doesn't once its field (or item) was taken out with x.
    let effective = accepted[m.key] || null;
    if (!effective && m.added && m.target && !dismissed[dismissKeyFor(m.target, m.key)]) effective = m.target;
    return { ...m, effective, suggested: m.target, accepted: !!accepted[m.key] };
  });

  // One automatic Income source per employer: a W-2 and a benefits summary
  // from the same employer would otherwise count the salary twice. The most
  // authoritative document wins; the other stays under "not sure".
  // (A source the client added with + is theirs and doesn't compete.)
  const bestByEmployer = new Map();
  for (const m of entries) {
    if (m.effective !== INCOME_SOURCE || m.accepted) continue;
    const employer = firstWord(m.label);
    const best = bestByEmployer.get(employer);
    if (!best || m.priority < best.priority) bestByEmployer.set(employer, m);
  }
  return entries.map((m) => {
    if (m.effective !== INCOME_SOURCE || m.accepted || bestByEmployer.get(firstWord(m.label)) === m) return m;
    return { ...m, effective: null };
  });
}

/** Annual amount per regular "section.field" (special targets are handled apart). */
export function importTotals(files, state = {}) {
  const totals = {};
  for (const m of importMerchants(files, state)) {
    if (!m.effective || isSpecial(m.effective)) continue;
    totals[m.effective] = (totals[m.effective] || 0) + m.annual;
  }
  return totals;
}

/**
 * Deposit payers combined per account like spending: key -> { key, label,
 * kind, annual, months }. A payer counts when it's at least $1,000/yr and
 * seen in two or more months — or comes from a single statement, where one
 * month is all there is.
 */
export function importIncome(files) {
  const payers = new Map();
  for (const group of importGroups(files)) {
    for (const file of group.files) {
      for (const p of incomeOf(file)) {
        const entry = payers.get(p.key) || { key: p.key, label: p.label, kind: p.kind, annual: 0, months: new Set(), files: 0 };
        entry.annual += (Number(p.raw) || 0) * group.factor;
        (p.months || []).forEach((month) => entry.months.add(month));
        entry.files += 1;
        payers.set(p.key, entry);
      }
    }
  }
  const result = new Map();
  for (const p of payers.values()) {
    if (p.annual >= MIN_INCOME_PER_YEAR && (p.months.size >= 2 || p.files === 1)) {
      result.set(p.key, { key: p.key, label: p.label, kind: p.kind, annual: p.annual, months: p.months.size });
    }
  }
  return result;
}

function incomeName(payer) {
  return payer.kind === 'wages' ? `${payer.label} wages` : payer.label;
}

function sameName(a, b) {
  return (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
}

function firstWord(text) {
  return merchantKey(text).split(' ')[0] || '';
}

/** Income sources: gross amounts from documents, names from deposits. Mutates `applied`. */
function applyIncome(next, files, merchants, previous, applied, dismissed) {
  const payers = importIncome(files);
  const documented = new Map(
    merchants.filter((m) => m.effective === INCOME_SOURCE).map((m) => [`doc:${m.key}`, m]),
  );
  const income = next.income || {};
  let sources = (income.sources || []).map((s) => ({ ...s }));
  const before = JSON.stringify(sources);
  let added = 0;

  // Withdraw import-owned sources that are gone, if the client left them as they were.
  sources = sources.filter((s) => {
    if (!s._import_key) return true;
    if (s._import_key.startsWith('doc:')) {
      if (documented.has(s._import_key)) return true;
      const prior = previous[`income:${s._import_key}`];
      return !(prior && sameName(s.name, prior.name) && Number(s.amount) === prior.amount);
    }
    if (payers.has(s._import_key) && !dismissed[`income:${s._import_key}`]) return true;
    const untouched = sameName(s.name, previous[`income:${s._import_key}`]) && isEmpty(s.amount);
    return !untouched;
  });

  function place(source) {
    const blank = sources.findIndex((s) => isEmpty(s.name) && isEmpty(s.amount) && !s._import_key);
    if (blank >= 0) sources[blank] = { ...sources[blank], ...source };
    else if (sources.length < 20) sources.push(source);
    else return false;
    return true;
  }

  // Sources read from documents (W-2s, pay stubs): name and gross amount.
  for (const [key, m] of documented) {
    const amount = Math.round(m.annual);
    const appliedKey = `income:${key}`;
    const existing = sources.find((s) => s._import_key === key);
    if (existing) {
      const prior = previous[appliedKey];
      if (prior && Number(existing.amount) === prior.amount && sameName(existing.name, prior.name)) {
        existing.amount = amount;
        applied[appliedKey] = { name: existing.name, amount };
      }
      continue;
    }
    if (sources.some((s) => sameName(s.name, m.label))) continue;
    if (place({ name: m.label, amount, _import_key: key })) {
      applied[appliedKey] = { name: m.label, amount };
      added += 1;
    }
  }

  // Names suggested from deposits (take-home, so no amount).
  const documentedWords = new Set([...documented.values()].map((m) => firstWord(m.label)));
  for (const payer of payers.values()) {
    const key = `income:${payer.key}`;
    const name = incomeName(payer);
    if (dismissed[key]) continue;
    if (sources.some((s) => s._import_key === payer.key)) {
      applied[key] = previous[key] || name;
      continue;
    }
    if (key in previous) { // suggested before and since removed by the client
      applied[key] = previous[key];
      continue;
    }
    if (documentedWords.has(firstWord(payer.label))) continue; // a W-2 already covers this payer
    if (sources.some((s) => sameName(s.name, name))) continue;
    if (place({ name, amount: null, _import_key: payer.key })) {
      applied[key] = name;
      added += 1;
    }
  }

  if (JSON.stringify(sources) !== before) next.income = { ...income, sources };
  return added;
}

/** Auto lease / loan payments read from documents fill free auto slots. Mutates `applied`. */
function applyAutoLoans(next, merchants, previous, applied) {
  const loans = new Map(merchants.filter((m) => m.effective === AUTO_LOAN).map((m) => [m.key, m]));
  const section = { ...(next.auto_transport || {}) };
  const before = JSON.stringify(section);
  for (const slot of AUTO_SLOTS) {
    const current = section[slot];
    if (!current?._import_key) continue;
    const prior = previous[`auto:${current._import_key}`];
    const untouched = prior !== undefined && Number(current.amount) === prior;
    if (!loans.has(current._import_key)) {
      if (untouched) section[slot] = {};
    } else if (untouched) {
      const loan = loans.get(current._import_key);
      section[slot] = { ...current, amount: Math.round(loan.annual), is_lease: loan.lease };
      applied[`auto:${loan.key}`] = Math.round(loan.annual);
    }
  }
  for (const loan of loans.values()) {
    if (AUTO_SLOTS.some((slot) => section[slot]?._import_key === loan.key)) continue;
    const free = AUTO_SLOTS.find((slot) => isEmpty(section[slot]?.amount));
    if (!free) continue;
    section[free] = { amount: Math.round(loan.annual), is_lease: loan.lease, _import_key: loan.key };
    applied[`auto:${loan.key}`] = Math.round(loan.annual);
  }
  if (JSON.stringify(section) !== before) next.auto_transport = section;
}

/**
 * Re-apply all imported files to formData.
 *
 * Returns { formData, filled, kept, incomeAdded }: the updated data, how many
 * fields now hold imported amounts, how many imported amounts were not
 * applied because the client had already entered that field, and how many
 * Income sources were added.
 */
export function applyImports(formData) {
  const state = importState(formData);
  const { files, applied: previous, accepted, dismissed } = state;
  const merchants = importMerchants(files, state);
  const totals = importTotals(files, state);
  const fieldKeys = Object.keys(previous).filter((k) => !k.includes(':'));
  const keys = new Set([...Object.keys(totals), ...fieldKeys]);
  const payrollTargets = new Set(merchants.filter((m) => m.effective && m.payroll).map((m) => m.effective));

  const next = { ...formData };
  const applied = {};
  let filled = 0;
  let kept = 0;

  for (const key of keys) {
    const [section, field] = key.split('.');
    const sectionData = next[section] || {};
    const current = sectionData[field];
    const ownedByImport =
      isEmpty(current) || (key in previous && Number(current) === previous[key]);

    if (!ownedByImport) {
      if (totals[key]) kept += 1;
      continue;
    }

    const flag = IMPORT_AUTO_FLAGS[key];
    const total = Math.round(totals[key] || 0);
    if (total > 0) {
      const { _skipped, ...rest } = sectionData;
      const updated = { ...rest, [field]: total };
      if (flag && payrollTargets.has(key) && !rest[flag]) {
        updated[flag] = true;
        applied[`flag:${key}`] = true;
      } else if (flag && previous[`flag:${key}`] && rest[flag]) {
        applied[`flag:${key}`] = true;
      }
      next[section] = updated;
      applied[key] = total;
      filled += 1;
    } else if (!isEmpty(current)) {
      // The file (or entry) that supplied this amount is gone, or was removed with x.
      const updated = { ...sectionData, [field]: null };
      if (flag && previous[`flag:${key}`]) updated[flag] = false;
      next[section] = updated;
    }
  }

  const incomeAdded = applyIncome(next, files, merchants, previous, applied, dismissed);
  applyAutoLoans(next, merchants, previous, applied);
  next.import = { files, applied, accepted, dismissed };
  return { formData: next, filled, kept, incomeAdded };
}

/** Add one checked file and re-apply. `file` needs a unique `id`. */
export function addImport(formData, file) {
  const state = importState(formData);
  return applyImports({ ...formData, import: { ...state, files: [...state.files, file] } });
}

/** Remove a file by id and re-apply. */
export function removeImport(formData, fileId) {
  const state = importState(formData);
  return applyImports({
    ...formData,
    import: { ...state, files: state.files.filter((file) => file.id !== fileId) },
  });
}

/** x: remove something the import added ("section.field", "item:<key>" or "income:<payer>"). */
export function dismissImport(formData, dismissKey) {
  const state = importState(formData);
  const accepted = { ...state.accepted };
  // Taking out a field also forgets the + choices that fed it.
  for (const [key, target] of Object.entries(accepted)) {
    if (dismissKeyFor(target, key) === dismissKey) delete accepted[key];
  }
  return applyImports({
    ...formData,
    import: { ...state, accepted, dismissed: { ...state.dismissed, [dismissKey]: true } },
  });
}

/**
 * +: add a "not sure" entry to `target` ("section.field"). Only that entry
 * is added — a field taken out with x stays out for everything else.
 */
export function acceptImport(formData, entryKey, target) {
  const state = importState(formData);
  const dismissed = { ...state.dismissed };
  delete dismissed[`item:${entryKey}`];
  return applyImports({
    ...formData,
    import: { ...state, dismissed, accepted: { ...state.accepted, [entryKey]: target } },
  });
}

function describe(target) {
  const t = IMPORT_TARGETS[target];
  return { label: t?.label || target, sectionLabel: t?.sectionLabel || '' };
}

/**
 * What the import did, for the summary on the import page:
 *   added   — what now fills the form: { dismissKey, label, sectionLabel, amount, files, note }
 *   kept    — fields left alone because the client had entered them:
 *             { key, label, sectionLabel, yours, statements }
 *   unsure  — entries we didn't add: { key, label, category, annual, monthly, suggested }
 *   notices — review notices from files read without the model check
 * Lists are largest first.
 */
export function importSummary(formData) {
  const state = importState(formData);
  const { files, applied } = state;
  const merchants = importMerchants(files, state);
  const totals = importTotals(files, state);

  const added = [];
  for (const [key, amount] of Object.entries(applied)) {
    if (key.includes(':')) continue;
    const contributors = merchants.filter((m) => m.effective === key);
    added.push({
      dismissKey: key, ...describe(key), amount,
      files: [...new Set(contributors.flatMap((m) => m.files))],
    });
  }
  for (const m of merchants) {
    if (m.effective === INCOME_SOURCE && applied[`income:doc:${m.key}`]) {
      added.push({ dismissKey: `item:${m.key}`, label: m.label, sectionLabel: 'Income', amount: applied[`income:doc:${m.key}`].amount, files: m.files });
    }
    if (m.effective === AUTO_LOAN && applied[`auto:${m.key}`] !== undefined) {
      added.push({ dismissKey: `item:${m.key}`, label: m.label, sectionLabel: 'Auto Lease / Loan Payment', amount: applied[`auto:${m.key}`], files: m.files });
    }
  }
  const sources = formData.income?.sources || [];
  for (const [key, name] of Object.entries(applied)) {
    if (!key.startsWith('income:') || key.startsWith('income:doc:')) continue;
    if (!sources.some((s) => sameName(s.name, name))) continue;
    added.push({ dismissKey: key, label: name, sectionLabel: 'Income', amount: null, files: [], note: 'Enter the gross amount on the Income page.' });
  }
  added.sort((a, b) => (b.amount || 0) - (a.amount || 0));

  const kept = Object.entries(totals)
    .filter(([key, total]) => !(key in applied) && Math.round(total) > 0)
    .map(([key, total]) => {
      const [section, field] = key.split('.');
      return { key, ...describe(key), yours: formData[section]?.[field], statements: Math.round(total) };
    })
    .filter((k) => !isEmpty(k.yours))
    .sort((a, b) => b.statements - a.statements);

  const unsure = merchants
    .filter((m) => !m.effective && m.annual >= 1)
    .sort((a, b) => b.annual - a.annual);

  const notices = [...new Set(files.map((f) => f.review_notice).filter(Boolean))];
  return {
    added,
    kept,
    unsure,
    unsureTotal: unsure.reduce((sum, m) => sum + m.annual, 0),
    notices,
  };
}

/**
 * The already-imported file that `result` repeats, if any: the same account
 * and statement period, or (with no account) the same name, period and total.
 * An undated document of an account already imported isn't a repeat: it is
 * kept, and the latest statement of the account counts (replacedFiles).
 */
export function findDuplicate(files, result) {
  return (files || []).find((f) => {
    if (f.period_start !== result.period_start || f.period_end !== result.period_end) return false;
    const dated = Boolean(result.period_start && result.period_end);
    if (f.account_key && result.account_key && (dated || result.doc_kind !== 'document')) {
      return f.account_key === result.account_key;
    }
    const raw = (x) => merchantsOf(x).reduce((sum, m) => sum + (Number(m.raw) || 0), 0);
    return f.filename === result.filename && Math.abs(raw(f) - raw(result)) < 0.01;
  });
}
